import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ThumbsUp, ThumbsDown, Camera, X, Check, AlertCircle } from 'lucide-react';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, orderBy, limit, serverTimestamp, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

interface Review {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string;
  serviceId: string;
  serviceName: string;
  orderId?: string; // Optional
  rating: number;
  title?: string;
  comment: string;
  images?: string[]; // Changed from photos
  createdAt: string; // Changed from any - it's an ISO string
  updatedAt: string;
  helpful: number; // Changed from helpfulCount
  notHelpful: number; // Changed from notHelpfulCount
  verified: boolean; // Changed from verifiedPurchase
  status: 'pending' | 'approved' | 'rejected';
}

interface ReviewFormProps {
  orderId: string;
  serviceId: string;
  serviceName: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ReviewForm: React.FC<ReviewFormProps> = ({
  orderId,
  serviceId,
  serviceName,
  onSuccess,
  onCancel
}) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoURLs, setPhotoURLs] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) {
      setError('Maximum 5 photos allowed');
      return;
    }

    setPhotos([...photos, ...files]);
    
    // Create preview URLs
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoURLs(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_: File, i: number) => i !== index));
    setPhotoURLs(photoURLs.filter((_: string, i: number) => i !== index));
  };

  const uploadPhotos = async (): Promise<string[]> => {
    const uploadPromises = photos.map(async (photo) => {
      const photoRef = ref(storage, `reviews/${orderId}/${Date.now()}_${photo.name}`);
      await uploadBytes(photoRef, photo);
      return getDownloadURL(photoRef);
    });

    return Promise.all(uploadPromises);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in to submit a review');
      return;
    }

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Upload photos if any
      let photoUrls: string[] = [];
      if (photos.length > 0) {
        photoUrls = await uploadPhotos();
      }

      // Add review to Firestore
      await addDoc(collection(db, 'reviews'), {
        userId: user.uid,
        userName: user.displayName || 'Anonymous',
        userPhoto: user.photoURL || null,
        serviceId,
        serviceName,
        orderId,
        rating,
        comment,
        photos: photoUrls,
        helpfulCount: 0,
        notHelpfulCount: 0,
        verifiedPurchase: true,
        createdAt: serverTimestamp()
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting review:', err);
      setError(err.message || 'Failed to submit review');
    } finally {
      setUploading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <Check className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">Your review has been submitted successfully</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Rate Your Experience</h3>
        <p className="text-sm text-gray-600 mb-4">Service: {serviceName}</p>
        
        {/* Star Rating */}
        <div className="flex items-center space-x-2 mb-6">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="focus:outline-none transition-transform hover:scale-110"
            >
              <Star
                className={`h-10 w-10 ${
                  star <= (hoverRating || rating)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
          {rating > 0 && (
            <span className="ml-4 text-lg font-medium text-gray-700">
              {rating === 5 ? 'Excellent!' : rating === 4 ? 'Good' : rating === 3 ? 'Average' : rating === 2 ? 'Below Average' : 'Poor'}
            </span>
          )}
        </div>
      </div>

      {/* Comment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Review (Optional)
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your experience with this service..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          rows={5}
          maxLength={1000}
        />
        <p className="mt-1 text-xs text-gray-500">{comment.length}/1000 characters</p>
      </div>

      {/* Photo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Add Photos (Optional)
        </label>
        <div className="flex flex-wrap gap-4">
          {photoURLs.map((url: string, index: number) => (
            <div key={index} className="relative">
              <img
                src={url}
                alt={`Preview ${index + 1}`}
                className="w-24 h-24 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          
          {photos.length < 5 && (
            <label className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary-500 transition-colors">
              <Camera className="h-8 w-8 text-gray-400" />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </label>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">Add up to 5 photos</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Buttons */}
      <div className="flex space-x-4">
        <button
          type="submit"
          disabled={uploading || rating === 0}
          className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? 'Submitting...' : 'Submit Review'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

interface ReviewsListProps {
  serviceId: string;
  limit?: number;
}

export const ReviewsList: React.FC<ReviewsListProps> = ({ serviceId, limit: reviewLimit = 10 }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [userVotes, setUserVotes] = useState<Record<string, 'helpful' | 'notHelpful'>>({});

  useEffect(() => {
    fetchReviews();
  }, [serviceId]);

  const fetchReviews = async () => {
    try {
      // Query reviews for this service (status filter for approved reviews)
      const q = query(
        collection(db, 'reviews'),
        where('serviceId', '==', serviceId),
        where('status', '==', 'approved')
      );

      const snapshot = await getDocs(q);
      let reviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Review[];

      // Sort by createdAt on client side (newest first)
      reviewsData = reviewsData.sort((a, b) => {
        const dateA = typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() : 0;
        const dateB = typeof b.createdAt === 'string' ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      // Limit results on client side
      reviewsData = reviewsData.slice(0, reviewLimit);

      setReviews(reviewsData);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (reviewId: string, voteType: 'helpful' | 'notHelpful') => {
    if (!user) return;

    try {
      const reviewRef = doc(db, 'reviews', reviewId);
      const voteRef = doc(db, 'reviews', reviewId, 'votes', user.uid);

      const currentVote = userVotes[reviewId];

      if (currentVote === voteType) {
        // Remove vote
        await updateDoc(reviewRef, {
          [voteType]: increment(-1)
        });
        setUserVotes(prev => {
          const updated = { ...prev };
          delete updated[reviewId];
          return updated;
        });
      } else {
        // Add or change vote
        if (currentVote) {
          await updateDoc(reviewRef, {
            [currentVote]: increment(-1),
            [voteType]: increment(1)
          });
        } else {
          await updateDoc(reviewRef, {
            [voteType]: increment(1)
          });
        }
        setUserVotes(prev => ({ ...prev, [reviewId]: voteType }));
      }

      fetchReviews();
    } catch (err) {
      console.error('Error voting on review:', err);
    }
  };

  const getRatingBreakdown = () => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      breakdown[review.rating as keyof typeof breakdown]++;
    });
    return breakdown;
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  if (loading) {
    return <div className="text-center py-8">Loading reviews...</div>;
  }

  const ratingBreakdown = getRatingBreakdown();
  const averageRating = getAverageRating();

  return (
    <div className="space-y-4 md:space-y-5">
      {/* Rating Summary - Compact layout */}
      <div className="bg-gradient-to-br from-gray-50 to-primary-50 rounded-lg md:rounded-xl p-4 md:p-5 lg:p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row items-start justify-between gap-4 md:gap-5">
          {/* Average Rating */}
          <div className="text-center md:text-left flex-shrink-0 w-full md:w-auto">
            <div className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-br from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {averageRating}
            </div>
            <div className="flex items-center justify-center md:justify-start mt-1.5 md:mt-2">
              {[...Array(5)].map((_, i: number) => (
                <Star
                  key={i}
                  className={`h-4 w-4 md:h-5 md:w-5 ${
                    i < Math.round(parseFloat(averageRating.toString()))
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-gray-600 mt-1.5 md:mt-2 text-xs md:text-sm font-medium">
              Based on {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
            </p>
          </div>

          {/* Rating Breakdown - Full width */}
          <div className="flex-1 w-full space-y-1.5 md:space-y-2">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = ratingBreakdown[rating as keyof typeof ratingBreakdown];
              const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;

              return (
                <div key={rating} className="flex items-center gap-1.5 md:gap-2">
                  <div className="flex items-center gap-0.5 md:gap-1 w-10 md:w-12 flex-shrink-0">
                    <span className="text-xs md:text-sm font-medium text-gray-700">{rating}</span>
                    <Star className="h-3 w-3 md:h-3.5 md:w-3.5 fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-2 md:h-2.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-2 md:h-2.5 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs md:text-sm font-medium text-gray-600 w-8 md:w-10 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reviews List - Single column for better readability */}
      <div className="space-y-3 md:space-y-4">
        {reviews.map((review) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 hover:border-primary-300 rounded-lg md:rounded-xl p-3 md:p-5 transition-all duration-300 hover:shadow-lg"
          >
            <div className="flex items-start justify-between mb-2 md:mb-3">
              <div className="flex items-center space-x-2 md:space-x-3 flex-1">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-700 font-bold text-sm md:text-base">
                    {review.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900 text-xs md:text-sm truncate">{review.userName}</h4>
                    {review.verified && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                        <Check className="h-2.5 w-2.5 md:h-3 md:w-3 mr-0.5" />
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 md:gap-2 mt-0.5">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-3 w-3 md:h-3.5 md:w-3.5 ${
                            i < review.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] md:text-xs text-gray-500">
                      {new Date(review.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Review Title (if available) */}
            {review.title && (
              <h5 className="font-semibold text-gray-900 mb-1.5 md:mb-2 text-xs md:text-sm">{review.title}</h5>
            )}

            {/* Review Content */}
            <p className="text-gray-700 leading-relaxed mb-2 md:mb-3 line-clamp-4 text-xs md:text-sm">
              {review.comment}
            </p>

            {/* Review Photos */}
            {review.images && review.images.length > 0 && (
              <div className="flex flex-wrap gap-1.5 md:gap-2 mb-2 md:mb-3">
                {review.images.map((image: string, index: number) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Review photo ${index + 1}`}
                    className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity hover:scale-105 transform duration-300"
                  />
                ))}
              </div>
            )}

            {/* Helpful Buttons */}
            <div className="flex items-center gap-2 md:gap-3 pt-2 md:pt-3 border-t border-gray-100">
              <span className="text-[10px] md:text-xs text-gray-600 font-medium">Helpful?</span>
              <button
                onClick={() => handleVote(review.id, 'helpful')}
                className={`flex items-center gap-1 text-[10px] md:text-xs px-2 md:px-2.5 py-1 rounded-md transition-all ${
                  userVotes[review.id] === 'helpful'
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                }`}
              >
                <ThumbsUp className="h-3 w-3 md:h-3.5 md:w-3.5" />
                <span>{review.helpful}</span>
              </button>
              <button
                onClick={() => handleVote(review.id, 'notHelpful')}
                className={`flex items-center gap-1 text-[10px] md:text-xs px-2 md:px-2.5 py-1 rounded-md transition-all ${
                  userVotes[review.id] === 'notHelpful'
                    ? 'bg-red-50 text-red-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-red-600'
                }`}
              >
                <ThumbsDown className="h-3 w-3 md:h-3.5 md:w-3.5" />
                <span>{review.notHelpful}</span>
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* No Reviews State */}
      {reviews.length === 0 && (
        <div className="text-center py-8 md:py-12 bg-gray-50 rounded-lg md:rounded-xl">
          <div className="bg-gradient-to-br from-primary-100 to-secondary-100 w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
            <Star className="h-6 w-6 md:h-8 md:w-8 text-primary-600" />
          </div>
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">No reviews yet</h3>
          <p className="text-sm text-gray-600">Be the first to share your experience with this service!</p>
        </div>
      )}
    </div>
  );
};
